import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  LinearProgress,
  Typography,
  Collapse
} from '@mui/material';
import {
  Save as SaveIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { inventoryService } from '../services/InventoryService';
import { Item } from '../models/Item';
import { getCategoriesForShop } from '../utils/categories';

const EditItemDialog = ({ open, onClose, item, shopName, onUpdate }) => {
  const [editedItem, setEditedItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showPriceWarning, setShowPriceWarning] = useState(false);
  const [customCategory, setCustomCategory] = useState('');
  const [isCustomCategory, setIsCustomCategory] = useState(false);

  const categories = getCategoriesForShop(shopName || 'Az Tires');

  useEffect(() => {
    if (open && item) {
      const sellingPrice = item.sellingPrice || 0;
      const itemCategory = item.category || 'Tire';

      // Check if the current category is a custom one (not in the predefined list)
      const isCustom = !categories.includes(itemCategory) || itemCategory === 'Other';

      setEditedItem({
        name: item.name || '',
        brand: item.brand || '',
        category: isCustom && itemCategory !== 'Other' ? 'Other' : itemCategory,
        description: item.description || '',
        quantity: item.quantity || 0,
        sellingPrice: sellingPrice,
        originalCost: item.originalCost || 0
      });

      setIsCustomCategory(isCustom && itemCategory !== 'Other');
      setCustomCategory(isCustom && itemCategory !== 'Other' ? itemCategory : '');
      setShowPriceWarning(sellingPrice <= 0);
      setError(null);
    }
  }, [open, item, categories]);

  const handleFieldChange = (field, value) => {
    setEditedItem({
      ...editedItem,
      [field]: value
    });

    if (field === 'sellingPrice') {
      const price = parseFloat(value) || 0;
      setShowPriceWarning(price <= 0);
    }

    if (field === 'category') {
      if (value === 'Other') {
        setIsCustomCategory(true);
      } else {
        setIsCustomCategory(false);
        setCustomCategory('');
      }
    }
  };

  const getEffectiveCategory = () => {
    if (isCustomCategory && customCategory.trim()) {
      return customCategory.trim();
    }
    return editedItem?.category || 'Other';
  };

  const validate = () => {
    if (!editedItem.name || editedItem.name.trim() === '') {
      setError('Name is required');
      return false;
    }
    if (editedItem.quantity < 0) {
      setError('Quantity cannot be negative');
      return false;
    }
    if (editedItem.sellingPrice < 0) {
      setError('Selling price cannot be negative');
      return false;
    }
    if (editedItem.originalCost < 0) {
      setError('Original cost cannot be negative');
      return false;
    }
    // If category is "Other" and custom text is provided, use the custom text
    // If "Other" is selected but no custom text, that's fine — it stays as "Other"
    setError(null);
    return true;
  };

  const handleSave = async () => {
    if (!validate()) {
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const effectiveCategory = getEffectiveCategory();

      const updatedItem = new Item({
        ...item,
        name: editedItem.name.trim(),
        brand: editedItem.brand.trim(),
        category: effectiveCategory,
        description: editedItem.description.trim(),
        quantity: parseInt(editedItem.quantity) || 0,
        sellingPrice: parseFloat(editedItem.sellingPrice) || 0,
        originalCost: parseFloat(editedItem.originalCost) || 0
      });

      await inventoryService.updateItem(updatedItem);

      if (onUpdate) {
        await onUpdate();
      }

      handleClose();
    } catch (err) {
      setError(err.message || 'Failed to update item');
      console.error('Error updating item:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setEditedItem(null);
    setError(null);
    setCustomCategory('');
    setIsCustomCategory(false);
    onClose();
  };

  if (!editedItem) {
    return null;
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{
        background: '#E53935',
        color: 'white', pb: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 38, height: 38, borderRadius: 2,
            background: 'rgba(229,57,53,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <EditIcon sx={{ fontSize: 20 }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Edit Item</Typography>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
          <TextField
            label="Name"
            value={editedItem.name}
            onChange={(e) => handleFieldChange('name', e.target.value)}
            fullWidth
            required
            autoFocus
          />

          <TextField
            label="Brand"
            value={editedItem.brand}
            onChange={(e) => handleFieldChange('brand', e.target.value)}
            fullWidth
          />

          <FormControl fullWidth>
            <InputLabel>Category</InputLabel>
            <Select
              value={editedItem.category}
              label="Category"
              onChange={(e) => handleFieldChange('category', e.target.value)}
            >
              {categories.map(cat => (
                <MenuItem key={cat} value={cat}>{cat}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Custom category input — appears when "Other" is selected */}
          <Collapse in={editedItem.category === 'Other'}>
            <TextField
              label="Custom Category Name"
              value={customCategory}
              onChange={(e) => setCustomCategory(e.target.value)}
              fullWidth
              placeholder="e.g. Accessories, Tools, etc."
              helperText="Type a custom category name, or leave empty to use 'Other'"
              sx={{ mt: -0.5 }}
            />
          </Collapse>

          <TextField
            label="Description"
            value={editedItem.description}
            onChange={(e) => handleFieldChange('description', e.target.value)}
            fullWidth
            multiline
            rows={3}
          />

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
            <TextField
              label="Quantity"
              type="number"
              value={editedItem.quantity}
              onChange={(e) => handleFieldChange('quantity', e.target.value)}
              fullWidth
              inputProps={{ min: 0, step: 1, style: { fontSize: '16px' } }}
            />

            <TextField
              label="Selling Price"
              type="number"
              value={editedItem.sellingPrice || ''}
              onChange={(e) => handleFieldChange('sellingPrice', e.target.value)}
              fullWidth
              inputProps={{ min: 0, step: 0.01, style: { fontSize: '16px' } }}
              placeholder="Optional"
            />

            <TextField
              label="Original Cost"
              type="number"
              value={editedItem.originalCost}
              onChange={(e) => handleFieldChange('originalCost', e.target.value)}
              fullWidth
              inputProps={{ min: 0, step: 0.01, style: { fontSize: '16px' } }}
            />
          </Box>

          {showPriceWarning && (
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              Selling price is empty. You'll need to enter the price when selling this item.
            </Alert>
          )}
        </Box>

        {saving && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress sx={{ borderRadius: 2 }} />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={handleClose} disabled={saving} sx={{ fontWeight: 600 }}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={saving}
          sx={{
            background: '#E53935',
            '&:hover': {
              background: '#C62828',
            },
          }}
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditItemDialog;
